import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import { motion } from "framer-motion";

interface StatsCardProps {
  title: string;
  value: string;
  subtitle?: string;
  secondLine?: string;
  icon: LucideIcon;
  trend?: { value: number; positive: boolean };
  variant?: "default" | "accent" | "gold";
}

export function StatsCard({ title, value, subtitle, secondLine, icon: Icon, trend, variant = "default" }: StatsCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card variant={variant === "default" ? "elevated" : variant} className="relative overflow-hidden">
        {variant !== "default" && (
          <div className="absolute inset-0 bg-gradient-to-br from-transparent to-background/50" />
        )}
        <CardContent className="relative p-2 sm:p-3 md:p-5">
          <div className="flex flex-col gap-0.5 md:gap-1">
            <div className="flex items-center gap-1">
              <div className={cn(
                "flex h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 items-center justify-center rounded-lg flex-shrink-0",
                variant === "default" && "bg-secondary",
                variant === "accent" && "bg-accent/20",
                variant === "gold" && "bg-gold/20"
              )}>
                <Icon className={cn(
                  "h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4",
                  variant === "default" && "text-foreground",
                  variant === "accent" && "text-accent",
                  variant === "gold" && "text-gold"
                )} />
              </div>
              <p className="text-[10px] sm:text-xs md:text-sm font-medium text-muted-foreground truncate">{title}</p>
            </div>
            <p className={cn(
              "text-base sm:text-lg md:text-2xl font-bold tracking-tight",
              variant === "accent" && "text-accent",
              variant === "gold" && "text-gold"
            )}>
              {value}
            </p>
            {subtitle && (
              <p className="text-[9px] sm:text-[10px] md:text-xs text-muted-foreground truncate">{subtitle}</p>
            )}
            {secondLine && (
              <p className="text-[9px] sm:text-[10px] md:text-xs text-muted-foreground truncate">{secondLine}</p>
            )}
            {trend && (
              <p className={cn(
                "text-[9px] sm:text-[10px] md:text-xs font-medium",
                trend.positive ? "text-success" : "text-destructive"
              )}>
                {trend.positive ? "+" : ""}{trend.value}%
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}